import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 30000;
const DISMISS_KEY = "mw_email_health_dismissed_until";

/**
 * Catastrophic email-health watcher. Only mounts inside /admin (so it never
 * leaks to public visitors) and polls /admin/email-log/stats every 30s. If
 * the backend reports `catastrophic: true`, we surface a sticky red banner
 * at the very top of the admin shell with the reason + a link to the full
 * Email Health panel. Admin can dismiss it for 30 minutes.
 */
export default function EmailHealthAlert() {
  const { user } = useAuth();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [dismissedUntil, setDismissedUntil] = useState(0);
  const timerRef = useRef(null);

  const inAdmin = location.pathname.startsWith("/admin");
  const isAdminUser = user?.role === "admin";

  // Load dismissed-until from storage on mount.
  useEffect(() => {
    try {
      const raw = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (raw && raw > Date.now()) setDismissedUntil(raw);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    if (!inAdmin || !isAdminUser) {
      setAlert(null);
      return;
    }
    let alive = true;
    const tick = async () => {
      try {
        const r = await client.get(`/admin/email-log/stats?_=${Date.now()}`);
        if (!alive) return;
        if (r.data?.catastrophic) {
          setAlert({ reason: r.data.catastrophic_reason || "Email pipeline failure" });
        } else {
          setAlert(null);
        }
      } catch (e) { /* swallow */ }
    };
    tick();
    timerRef.current = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(timerRef.current); };
  }, [inAdmin, isAdminUser]);

  const dismiss = () => {
    const until = Date.now() + 30 * 60 * 1000;  // 30 min
    setDismissedUntil(until);
    try { localStorage.setItem(DISMISS_KEY, String(until)); } catch (e) { /* noop */ }
  };

  if (!inAdmin || !isAdminUser || !alert) return null;
  if (dismissedUntil > Date.now()) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[120] bg-[#FF3B30] text-white shadow-2xl border-b-2 border-black/30"
      data-testid="email-health-catastrophic-banner"
      role="alert"
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-3 flex items-start gap-3">
        <AlertTriangle size={20} strokeWidth={1.75} className="shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[10px] text-white/85">Email pipeline · catastrophic failure</div>
          <div className="text-sm leading-snug mt-0.5">{alert.reason}</div>
          <a
            href="/admin?tab=email-health"
            className="inline-block mt-1 text-xs underline underline-offset-2 hover:no-underline"
            data-testid="email-health-banner-link"
          >
            Open Email Health panel →
          </a>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss for 30 minutes"
          className="shrink-0 p-1 hover:bg-white/20 transition-colors"
          data-testid="email-health-banner-dismiss"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
